// /create — dashboard / landing page.
//
// First thing users see after sign-in (post-May-26 default). One section:
//
//   "Start a new creative" — the 4 category tiles (Product Shot / Fashion /
//   Carousel / Social Post), hoisted out of the wizard's old Step 1.
//   Clicking a tile navigates to /appv2?type=<id> which is the new
//   entry point for the accordion wizard.
//
// The legacy "Start with a template" Recreate grid was removed (June 9).
// Templates moved to their own /templates surface (sidebar entry); the
// dashboard is purely category-pick now.
//
// Lives in /appv2's layout family — uses AppLayoutV2 with hideNewPost
// (you're already on the New Creative page). No top-bar chrome, sidebar
// handles all global nav.
//
// /app's marketing CTAs + sign-in default now point here too; /app stays
// accessible by direct URL as a legacy fallback during the cutover window.

import { useNavigate } from 'react-router-dom';
import AppLayoutV2 from '@/components/feature/AppLayoutV2';
import { useCredits } from '@/hooks/useCredits';
import { CATEGORIES, type Category } from '@/pages/app/components/StepCategory';

// Dashboard-local card type — the Animation surface isn't a wizard
// PostType (it has its own /appv3/animation Studio, not an /appv2
// route), so its card lives here instead of widening the PostType
// union (which would ripple into every Record<PostType, …> in the
// wizard).
//
// Jun 26 — `generate-20` joins as another dashboard-only id. It's a
// shortcut into My Creatives' top-up flow (no wizard); click routes
// to /gallery with state hint that opens the existing topUpPickerOpen
// modal. Same union-widening trick as 'animation' so PostType stays
// clean.
type DashboardCard =
  | Category
  | (Omit<Category, 'id'> & { id: 'animation' })
  | (Omit<Category, 'id'> & { id: 'generate-20' })
  | (Omit<Category, 'id'> & { id: 'fake-ugc' })
  | (Omit<Category, 'id'> & { id: 'brand-imagery' });

const ANIMATION_CARD: DashboardCard = {
  id: 'animation',
  label: 'Animation',
  description:
    'Turn your creatives into a short MP4 — chain images together or animate a headline. Made for TikTok, Reels, and Stories.',
  icon: 'ri-film-line',
  previewSrc: '/ai-templates/product-shot/golden-splash.webp',
  fanImages: [
    '/ai-templates/product-shot/golden-splash.webp',
    '/ai-templates/product-shot/cinematic-liquid.webp',
    '/ai-templates/product-shot/still-water.webp',
  ],
  badge: 'NEW',
};

// Jun 26 — Generate 20 shortcut. Routes to My Creatives + opens the
// existing top-up picker on arrival. Not a wizard — pure deep-link.
const GENERATE_20_CARD: DashboardCard = {
  id: 'generate-20',
  label: 'Generate Ads',
  description:
    'Generate up to 20 fresh ads in one go. Pick your products and let AI fill your queue with new concepts.',
  icon: 'ri-stack-line',
  previewSrc: '/dashboard-tiles/generate-ads.webp',
  fanImages: [
    '/ai-templates/product-shot/editorial-luxe.webp',
    '/ai-templates/product-shot/candid-glow.webp',
    '/ai-templates/product-shot/playful-pastel.webp',
  ],
  badge: 'FAST',
};

// Aug 3 — Fake UGC. One-tap: pick a product → app renders it in a real
// creator/phone-shot aesthetic (backend UGC recipes, no template picker) and
// saves to My Creatives. Routes to the dedicated /ugc flow.
const FAKE_UGC_CARD: DashboardCard = {
  id: 'fake-ugc',
  label: 'Fake UGC',
  description:
    'Turn a product into an authentic-looking user-generated post — real lighting, casual feel, baked-in caption. Just pick a product.',
  icon: 'ri-camera-lens-line',
  previewSrc: '/dashboard-tiles/fake-ugc.webp',
  fanImages: [
    '/ai-templates/product-shot/candid-glow.webp',
    '/ai-templates/product-shot/playful-pastel.webp',
    '/ai-templates/product-shot/still-water.webp',
  ],
  badge: 'NEW',
};

// Aug 3 — Brand Imagery. Pick one of the brand's own scraped photos → generate
// 4 on-brand variations (with/without product, text preserved) → My Creatives.
// Routes to the dedicated /brand-imagery studio.
const BRAND_IMAGERY_CARD: DashboardCard = {
  id: 'brand-imagery',
  label: 'Brand Imagery',
  description:
    "Pick one of your brand's own photos and generate more shots in the exact same style — with or without your product.",
  icon: 'ri-gallery-line',
  previewSrc: '/dashboard-tiles/brand-imagery.webp',
  fanImages: [
    '/ai-templates/product-shot/editorial-luxe.webp',
    '/ai-templates/product-shot/still-water.webp',
    '/ai-templates/product-shot/cinematic-liquid.webp',
  ],
  badge: 'NEW',
};

export default function CreatePage() {
  const navigate = useNavigate();
  const { planTier } = useCredits();
  const isFree = planTier === 'free';

  const handleCategoryClick = (cat: DashboardCard) => {
    // Free-tier lock kept in place — no paid-only categories exist post
    // video-strip, but the hook stays so a future paid feature can
    // drop straight in.
    if (cat.paidOnly && isFree) {
      navigate('/checkout');
      return;
    }
    if (cat.id === 'animation') {
      navigate('/appv3/animation');
      return;
    }
    // Jun 26 — Generate 20 shortcut: deep-link to /gallery with a
    // router-state hint. Gallery reads this on mount + opens the
    // existing top-up picker modal. No wizard, no extra API surface.
    if (cat.id === 'generate-20') {
      navigate('/gallery', { state: { openTopUp: true } });
      return;
    }
    if (cat.id === 'fake-ugc') {
      navigate('/ugc');
      return;
    }
    if (cat.id === 'brand-imagery') {
      navigate('/brand-imagery');
      return;
    }
    // V3 Social Post (Jun 15) — the dashboard's Social Post card has
    // id='single' (legacy PostType union name). Map it to the new V3
    // route /appv3/social-post which surfaces the intent picker pre-step.
    // The /appv2 8-JSON-template path stays accessible via direct URL
    // (/appv2?type=single) for now; dashboard card pushes V3.
    if (cat.id === 'single') {
      navigate('/appv3/social-post');
      return;
    }
    // /appv3 covers product-shot, product-swap, fashion (In Hand + Worn
    // surface live there) AND carousel (Jun 14 — V3 Carousel route ships
    // alongside /appv2 carousel).
    const appv3Categories = new Set(['product-shot', 'product-swap', 'fashion', 'carousel']);
    if (appv3Categories.has(cat.id)) {
      navigate(`/appv3/${cat.id}`);
      return;
    }
    navigate(`/appv2?type=${cat.id}`);
  };

  return (
    <AppLayoutV2>
      {/* Outer scroll container. The sidebar is sticky / its own scroller;
          this column has its own scroll if content overflows. */}
      <div className="min-h-screen flex flex-col">
        {/* Top spacer so the headline sits visually centered above the
            tiles without pinning to the very top. The mesh blob below
            anchors the visual weight. */}
        <div className="flex-1 flex flex-col items-center justify-start px-6 py-12 lg:py-16">
          {/* 1500px matches /appv2's max-w so the category grid sits
              consistently with the wizard surfaces. */}
          <div className="w-full max-w-[1500px] mx-auto">

            {/* ---- HERO HEADER ---- */}
            <div className="text-center mb-8 lg:mb-10">
              <h1 className="font-display font-bold text-dark text-2xl lg:text-3xl mb-2">
                What are you creating today?
              </h1>
              <p className="text-dark-muted text-sm lg:text-base">
                Pick a workflow to start a new creative
              </p>
            </div>

            {/* ---- CATEGORY TILES ---- */}
            <CategoryTiles
              onSelect={handleCategoryClick}
              isFree={isFree}
            />

          </div>
        </div>
      </div>
    </AppLayoutV2>
  );
}


// =============================================================================
// CategoryTiles — hoisted from StepCategory.tsx's wizard step, restyled to
// work as a dashboard centerpiece rather than an inline wizard step.
// =============================================================================

interface CategoryTilesProps {
  onSelect: (cat: DashboardCard) => void;
  isFree: boolean;
}

// Fashion was previously hidden from the dashboard while it was
// scoped for retirement in favor of the product-swap route. Now that
// /appv3 surfaces Fashion as "Worn Item" with its own AI Magic mode
// (model freestyle-generated wearing the user's garment), it's back on
// the dashboard. Both In Hand (route id 'product-swap') and Fashion
// ("Worn Item") route to /appv3/<category> from the dashboard.
// Animation (Jun 11) appends at the end — dashboard-only card, routes
// to /appv3/animation (the standalone /animation URL redirects there
// for back-compat).
// Jun 12 v6 — In Hand ('product-swap') folded into Product Shot, so its
// dashboard card is dropped (its templates + poses live inside Product Shot
// now). The /appv3/product-swap URL still redirects to product-shot.
// Jun 18 — Carousel hidden from the dashboard for v1 (deferred). The route
// (/appv3/carousel), its recipe hero+variants render path, and all code stay
// intact + reachable by direct URL — just not advertised. Re-add 'carousel'
// here to bring the card back.
const DASHBOARD_CATEGORIES: DashboardCard[] = [
  // Jun 22 — Social Post (id='single') hidden from the dashboard. Route +
  // page + intent picker + INTENT_SCAFFOLDS backend wiring all stay in
  // place (reachable via direct URL /appv3/social-post + /appv2?type=single)
  // — just not advertised. Rationale: heavy overlap with Product Shot's
  // curated recipe library which already produces social-ready creatives
  // with baked text; unique intents (compare/social-proof/problem-promise)
  // need their own infrastructure (competitor input flow, testimonial
  // layouts) before they're launch-ready. Re-add 'single' here to bring
  // the card back.
  ...CATEGORIES.filter(
    (c) => c.id !== 'product-swap' && c.id !== 'carousel' && c.id !== 'single',
  ),
  FAKE_UGC_CARD,
  BRAND_IMAGERY_CARD,
  ANIMATION_CARD,
  GENERATE_20_CARD,
];

function CategoryTiles({ onSelect, isFree }: CategoryTilesProps) {
  return (
    // 4-up at desktop (was 5 — dropped Fashion), 3-up at lg, 2-up at sm,
    // single column on mobile.
    //
    // gap-20 desktop (80px) + each card constrained to max-w-[240px] +
    // mx-auto centering ⇒ much more breathing room between cards. The
    // grid cell is wider than the card, and the extra space distributes
    // around the card via mx-auto, so visible gap between cards =
    // (cell_pad_each_side × 2) + grid_gap. Net feel: cards look like
    // boutique posters with proper breathing room.
    //
    // `group/wall` lets non-hovered siblings recede (scale + fade) when
    // ANY card is hovered — gives the hovered deck a clear focal point
    // by demoting its neighbors.
    <div className="group/wall grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-x-4 gap-y-8 xl:gap-x-5 mb-10 lg:mb-12 px-2 lg:px-4 overflow-visible">
      {DASHBOARD_CATEGORIES.map((cat) => (
        <CategoryCard
          key={cat.id}
          cat={cat}
          isLocked={!!cat.paidOnly && isFree}
          onClick={() => onSelect(cat)}
        />
      ))}
    </div>
  );
}

// CategoryCard — one category as a single portrait image (4:5) with a soft
// drop shadow. The title + description sit BELOW the card so they're always
// legible (no text overlaid on the image). Hover lifts the card + deepens the
// shadow; sibling cards recede via the parent `group/wall` wrapper so the
// hovered card gets focus.
function CategoryCard({
  cat,
  isLocked,
  onClick,
}: {
  cat: DashboardCard;
  isLocked: boolean;
  onClick: () => void;
}) {
  // Mobile: the 2-line description gets cut off on narrow cards, so we hide it
  // and expose it via an info toggle (tap → popover). Desktop keeps the inline
  // 2-line teaser (it reads fine at that width).
  const [showInfo, setShowInfo] = useState(false);
  return (
    <button
      onClick={onClick}
      title={isLocked ? 'Upgrade to a paid plan to unlock this feature' : cat.label}
      className="group relative text-left flex flex-col cursor-pointer focus:outline-none transition-all duration-300 ease-out group-hover/wall:scale-95 group-hover/wall:opacity-50 hover:!scale-100 hover:!opacity-100 hover:z-20 max-w-[240px] mx-auto w-full"
    >
      {/* Single portrait image + soft drop shadow. Lifts + deepens shadow on
          hover; a thin ring gives the card a crisp edge against the surface. */}
      <div className="relative w-full aspect-square rounded-2xl overflow-hidden bg-surface ring-1 ring-surface-dark/40 shadow-lg shadow-dark/10 transition-all duration-300 ease-out group-hover:-translate-y-1.5 group-hover:shadow-2xl group-hover:shadow-primary/20 group-hover:ring-primary/40">
        <img
          src={cat.previewSrc}
          alt={`${cat.label} example`}
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
        {cat.badge && (
          <span className="absolute top-2.5 right-2.5 px-2 py-0.5 rounded-md bg-dark text-white text-[10px] font-bold shadow-md uppercase tracking-wide">
            {cat.badge}
          </span>
        )}
        {isLocked && (
          <div className="absolute top-2.5 left-2.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-bold shadow-sm">
            <i className="ri-lock-2-line text-xs" />
            Upgrade
          </div>
        )}
      </div>

      {/* Title + description BELOW the card — always legible on the page bg. */}
      <div className="pt-3 px-0.5 relative">
        <div className="flex items-start gap-1.5">
          <h3 className="flex-1 min-w-0 font-display font-bold text-dark text-lg leading-tight group-hover:text-primary transition-colors">
            {cat.label}
          </h3>
          {/* Mobile-only info toggle. role=button span (not a nested <button>,
              which is invalid inside the card <button>); stopPropagation so it
              doesn't trigger the card's navigate. */}
          <span
            role="button"
            tabIndex={0}
            aria-label={`About ${cat.label}`}
            aria-expanded={showInfo}
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); setShowInfo((v) => !v); }}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); e.preventDefault(); setShowInfo((v) => !v); } }}
            className="sm:hidden flex-shrink-0 mt-1 w-6 h-6 rounded-full flex items-center justify-center text-primary bg-primary/10 active:bg-primary/20 cursor-pointer"
          >
            <i className="ri-information-line text-sm" />
          </span>
        </div>
        {/* Desktop: inline 2-line teaser (fits at that width). Hidden on mobile. */}
        <p className="hidden sm:block mt-1 text-dark-muted text-xs leading-relaxed line-clamp-2">
          {cat.description}
        </p>
        {/* Mobile: full description in a tap-to-open popover (keeps cards short
            + grid rows aligned — an inline expand would offset the grid). */}
        {showInfo && (
          <>
            <div
              className="sm:hidden fixed inset-0 z-30"
              onClick={(e) => { e.stopPropagation(); e.preventDefault(); setShowInfo(false); }}
            />
            <div className="sm:hidden absolute z-40 left-0 right-0 top-full mt-1 rounded-xl bg-white border border-surface-dark/40 shadow-xl p-3">
              <p className="text-dark-muted text-xs leading-relaxed">
                {cat.description}
              </p>
            </div>
          </>
        )}
      </div>
    </button>
  );
}
